import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const { t, language, toggleLanguage } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false); // First-time setup mode
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Check if this is first-time setup (no admins exist)
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/setup-status`);
        // setup_required === true → no admins yet → show Initial Setup wizard
        // setup_required === false → at least one admin exists → show Login form
        setIsSetup(Boolean(response.data?.setup_required));
      } catch (error) {
        // If the endpoint is unreachable, fall back to login form (safer default)
        console.error('Setup-status check failed:', error);
        setIsSetup(false);
      } finally {
        setCheckingSetup(false);
      }
    };
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isSetup) {
        // First-time setup - create super admin
        const response = await axios.post(`${API_URL}/api/auth/setup`, {
          email: form.email,
          password: form.password,
          name: form.name
        });
        
        // Store token and navigate
        const { access_token, admin } = response.data;
        localStorage.setItem('padel-ladder-token', access_token);
        toast.success(language === 'el' ? 'Super Admin δημιουργήθηκε!' : 'Super Admin created!');
        navigate('/admin');
        window.location.reload(); // Refresh to update auth state
      } else {
        // Normal login
        await login(form.email, form.password);
        navigate('/admin');
      }
    } catch (error) {
      const message = error.response?.data?.detail || t('invalidCredentials');
      
      // If setup fails with "Setup already completed", switch to login mode
      if (message.includes('Setup already completed') || message.includes('already completed')) {
        setIsSetup(false);
        toast.info(language === 'el' ? 'Παρακαλώ συνδεθείτε' : 'Please login');
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1692288720754-743fbd1f2155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwcGFkZWwlMjBjb3VydCUyMGJsdWUlMjBhZXJpYWx8ZW58MHx8fHwxNzcyMTIxODk1fDA&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 bg-slate-900/90" />
      
      <div className="relative w-full max-w-md">
        {/* Language Toggle */}
        <div className="flex justify-center mb-6">
          <div className="lang-toggle bg-white/10 backdrop-blur-sm" data-testid="login-language-toggle">
            <button
              onClick={() => language !== 'en' && toggleLanguage()}
              className={`${language === 'en' ? 'active bg-white text-slate-900' : 'text-white'}`}
            >
              EN
            </button>
            <button
              onClick={() => language !== 'el' && toggleLanguage()}
              className={`${language === 'el' ? 'active bg-white text-slate-900' : 'text-white'}`}
            >
              EL
            </button>
          </div>
        </div>

        {/* Login/Setup Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center h-14 w-14 bg-slate-900 rounded-xl mb-4">
              {isSetup ? (
                <Sparkles className="h-7 w-7 text-white" />
              ) : (
                <Trophy className="h-7 w-7 text-white" />
              )}
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {isSetup 
                ? (language === 'el' ? 'Αρχική Ρύθμιση' : 'Initial Setup')
                : t('adminLogin')
              }
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isSetup 
                ? (language === 'el' ? 'Δημιουργία Super Admin' : 'Create Super Admin')
                : 'Padel Ladder'
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {isSetup && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Admin Name"
                  required
                  data-testid="setup-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@club.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading 
                ? t('loading') 
                : isSetup 
                  ? (language === 'el' ? 'Δημιουργία Super Admin' : 'Create Super Admin')
                  : t('loginButton')
              }
            </Button>
          </form>

          {/* Info text */}
          {isSetup && (
            <div className="mt-6 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
              {language === 'el' 
                ? 'Ο πρώτος admin θα είναι Super Admin με πλήρη δικαιώματα.'
                : 'The first admin will be a Super Admin with full permissions.'
              }
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-6">
          {t('poweredBy')} <span className="font-medium">Phidél</span>
        </p>
      </div>
    </div>
  );
}
